/**
 * Spacer cable sagging: a spacer cable's phases hang from the support cable above them at fixed offsets,
 * so each phase is sagged to the support cable's midspan height less its phase's vertical offset.
 * The offsets are configured per wire spec in the model's sag table details (`models/sag_tables/<specKey>`)
 * as `mode: 'spacer_cable_offsets'`, a `phase_count`, and `spacer_cable_offsets` keyed by phase index.
 */

/** The cable types a spacer cable can hang from */
export const SPACER_CABLE_SUPPORT_TYPES = ['Strand', 'Messenger', 'Static Wire'];

/** The symbols written after a phase letter, as in `AØ` */
const PHASE_SYMBOLS = /[ØøΦφ]/g;

/** A phase letter followed by a phase symbol, as in `AØ`, not run on from a preceding word */
const PHASE_LABEL = new RegExp(`(?:^|[^a-z])([abc])\\s?${PHASE_SYMBOLS.source}`, 'i');

/**
 * @typedef {Object} SpacerCablePhase
 * @property {number} index The phase's zero-based position in the wire spec's phase list.
 * @property {string} name The phase's configured name, or `Phase <n>` when it has none.
 * @property {number} vertical_offset How far the phase hangs below the support cable [in].
 * @property {number} horizontal_offset The phase's horizontal offset from the support cable [in].
 */

/**
 * Collects the phases of every wire spec configured with spacer cable offsets.
 * @param {?Object<string, Object>} sagTables The model's `models/sag_tables`, keyed by wire spec key.
 * @param {?Object<string, Object>} wireSpecs The model's wire specs, keyed by wire spec key.
 * @returns {Map<string, Array<SpacerCablePhase>>} The phases, in phase order, keyed by wire spec name.
 */
export function parseSpacerCableOffsets(sagTables, wireSpecs) {
  const phasesBySpec = new Map();
  for (const [specKey, details] of Object.entries(sagTables ?? {})) {
    if (details?.mode != 'spacer_cable_offsets') continue;
    const specName = wireSpecs?.[specKey]?.wire_spec ?? details.wire_spec;
    const phaseCount = parseInt(details.phase_count) || 0;
    if (!specName || phaseCount <= 0) continue;
    const phases = Array.from({ length: phaseCount }, (_, index) => {
      const stored = details.spacer_cable_offsets?.[index] ?? {};
      return {
        index,
        name: String(stored.name ?? '').trim() || `Phase ${index + 1}`,
        vertical_offset: parseFloat(stored.vertical_offset) || 0,
        horizontal_offset: parseFloat(stored.horizontal_offset) || 0
      };
    });
    phasesBySpec.set(specName, phases);
  }
  return phasesBySpec;
}

/**
 * A marker's current midspan height, including any proposed move.
 * @param {Object} marker A midspan wire marker.
 * @returns {?number} The height [in], or null when the marker has none.
 */
export function getMidspanHeight(marker) {
  const height = parseFloat(marker?._measured_height ?? marker?._manual_height);
  if (!Number.isFinite(height)) return null;
  return height + (parseFloat(marker?.mr_move) || 0);
}

/**
 * The path of the marker a pole marker is nested under, such as the insulator a wire is attached to.
 * @param {?string} path A dot-delimited marker path, as TraverseMarkers gives it.
 * @returns {?string} The parent marker's path, or null for a marker attached directly to the pole.
 */
function getParentPath(path) {
  return path?.split('.').slice(0, -3).join('.') || null;
}

/**
 * @param {number} feet A height [ft].
 * @returns {number} The height rounded to the nearest whole inch [in].
 */
function toWholeInches(feet) {
  return Math.round(feet * 12);
}

/**
 * @typedef {Object} SpanWire
 * @property {string} itemKey The midspan wire marker's key.
 * @property {number} n1Height The wire's attachment height on the first pole [ft].
 * @property {number} n2Height The wire's attachment height on the second pole [ft].
 * @property {string} [n1Path] The path of the wire's marker on the first pole.
 * @property {string} [n2Path] The path of the wire's marker on the second pole.
 */

/**
 * Finds the support cable a spacer cable wire hangs from: the nearest one attached at or above it on the poles.
 * Attachments are compared to the whole inch, so ones that round to the same inch count as the same height.
 * A support nested under the same parent marker (such as the same insulator) on either pole is the closest match.
 * @param {SpanWire} wire The spacer cable wire.
 * @param {Array<SpanWire>} supports The span's support cables.
 * @returns {?SpanWire} The support cable, or null when none is at or above the wire.
 */
export function findSpacerCableSupport(wire, supports) {
  let best = null;
  let bestScore = Infinity;
  for (const support of supports) {
    if (support.itemKey == wire.itemKey) continue;
    const sharesParent = ['n1Path', 'n2Path'].some((key) => {
      const parentPath = getParentPath(wire[key]);
      return parentPath != null && parentPath == getParentPath(support[key]);
    });
    // The support's average height above the wire across both poles [in]; a missing height makes it NaN, which is skipped
    const gap =
      (toWholeInches(support.n1Height) - toWholeInches(wire.n1Height) + (toWholeInches(support.n2Height) - toWholeInches(wire.n2Height))) /
      2;
    if (!sharesParent && !(gap >= 0)) continue;
    const score = sharesParent ? -Infinity : gap;
    if (score < bestScore) {
      best = support;
      bestScore = score;
    }
  }
  return best;
}

/**
 * Reduces a phase name to the letter or label that identifies it, so `AØ`, `a` and `Phase A` all read as `A`.
 * @param {*} name A phase name or phase attribute.
 * @returns {?string} The normalized name, or null when it is empty.
 */
export function normalizePhaseName(name) {
  const normalized = String(name ?? '')
    .replace(PHASE_SYMBOLS, '')
    .trim()
    .toUpperCase()
    .replace(/^PHASE\s*/, '');
  return normalized || null;
}

/**
 * The phase a wire is labelled with: an `AØ`, `BØ` or `CØ` in its trace's company or label,
 * or else the `phase` attribute of one of its markers or its trace.
 * @param {Array<?Object>} markers The wire's markers, checked in order.
 * @param {?Object} trace The wire's trace.
 * @returns {?string} The normalized phase name, or null when the wire has none.
 */
export function getWirePhaseName(markers, trace) {
  for (const text of [trace?.company, trace?.label]) {
    const match = String(text ?? '').match(PHASE_LABEL);
    if (match) return match[1].toUpperCase();
  }
  for (const source of [...markers, trace]) {
    const phase = normalizePhaseName(source?.phase);
    if (phase) return phase;
  }
  return null;
}

/**
 * Pairs each spacer cable wire with its phase.
 * Wires are grouped by the support cable they hang from, so each support's wires get their own set of phases,
 * and then by wire spec within that support, since each spec has its own phase list.
 * Within a group, a wire labelled with a phase (see getWirePhaseName) takes the phase of that name,
 * and the rest take the remaining phases in order, top to bottom by attachment height on the poles.
 * @param {Array<SpanWire & {marker: Object, n1Marker: ?Object, n2Marker: ?Object, trace: ?Object, phases: Array<SpacerCablePhase>, support: ?SpanWire}>} wires
 * @returns {Array<{wire: Object, phase: ?SpacerCablePhase}>} The wires in their given order,
 *   each with its phase, or null when it has no support cable or its spec has no phase left for it.
 */
export function assignSpacerCablePhases(wires) {
  /** @type {Map<Object, ?SpacerCablePhase>} */
  const phaseByWire = new Map();
  /** @type {Map<Object, Map<Array<SpacerCablePhase>, Array<Object>>>} */
  const groups = new Map();
  for (const wire of wires) {
    if (!wire.support) continue;
    const bySpec = groups.get(wire.support) ?? groups.set(wire.support, new Map()).get(wire.support);
    (bySpec.get(wire.phases) ?? bySpec.set(wire.phases, []).get(wire.phases)).push(wire);
  }
  for (const bySpec of groups.values()) {
    for (const [phases, groupWires] of bySpec) {
      const claimed = new Set();
      const unlabelled = [];
      for (const wire of groupWires) {
        const name = getWirePhaseName([wire.marker, wire.n1Marker, wire.n2Marker], wire.trace);
        const phase = name == null ? null : phases.find((candidate) => normalizePhaseName(candidate.name) == name);
        if (phase) {
          phaseByWire.set(wire, phase);
          claimed.add(phase);
        } else {
          unlabelled.push(wire);
        }
      }
      const remaining = phases.filter((phase) => !claimed.has(phase));
      unlabelled
        .sort((a, b) => b.n1Height + b.n2Height - (a.n1Height + a.n2Height))
        .forEach((wire, index) => phaseByWire.set(wire, remaining[index] ?? null));
    }
  }
  return wires.map((wire) => ({ wire, phase: phaseByWire.get(wire) ?? null }));
}
